'use strict';

/**
 * Pure taxonomy/tree helpers for the avoidances selector.
 *
 * Extracted verbatim from AvoidanceSelect.react.js (EAT-3929) with no behavior
 * change so the component can be decomposed and these algorithms unit-tested in
 * isolation. Every function here is pure except where noted (assignOriginalIndex
 * and markHasChildren mutate their argument, matching the original in-component
 * behavior).
 */

/** Keep only taxonomy nodes whose category_key is in ancestorKeysSet, recursively. */
export function filterTaxonomy(taxonomy, ancestorKeysSet) {
    return taxonomy.reduce((acc, item) => {
        if (ancestorKeysSet.has(item.category_key)) {
            const filteredChildren = filterTaxonomy((item.children || []), ancestorKeysSet);
            acc.push({ ...item, children: filteredChildren });
        }
        return acc;
    }, []);
}

/**
 * Flatten a filtered taxonomy into an indented list, attaching each matching
 * element (by ancestor_keys) beneath its leaf category.
 */
export function combineTaxonomyAndElements(filteredTaxonomy, responseElements) {
    const combinedArray = [];

    // An element can list several leaf categories in its ancestor_keys (e.g. cocoa
    // sits under both Hot Chocolate and Cocoa), which would otherwise attach it once
    // per matching leaf and render duplicate rows. Attach each element to only the
    // first matching leaf encountered in traversal order (EAT-3929).
    const seenElementUuids = new Set();

    function traverse(taxonomyNode, currentIndent) {
        combinedArray.push({ ...taxonomyNode, indents: currentIndent });

        if (taxonomyNode.children.length === 0) {
            const matchingElements = responseElements.filter(element =>
                (element.ancestor_keys || []).includes(taxonomyNode.category_key)
            );
            matchingElements.forEach(element => {
                if (element.uuid && seenElementUuids.has(element.uuid)) {
                    return;
                }
                if (element.uuid) {
                    seenElementUuids.add(element.uuid);
                }
                combinedArray.push({ ...element, indents: currentIndent + 1 });
            });
        } else {
            taxonomyNode.children.forEach(child => traverse(child, currentIndent + 1));
        }
    }

    filteredTaxonomy.forEach(node => traverse(node, 0));

    return combinedArray;
}

/** Split a flattened list into groups, each starting at an indent-0 row. */
export function groupCombinedArray(sortedArray) {
    const groupedArray = [];
    let currentGroup = [];

    sortedArray.forEach(item => {
        if (item.indents === 0) {
            if (currentGroup.length > 0) {
                groupedArray.push(currentGroup);
            }
            currentGroup = [item];
        } else {
            currentGroup.push(item);
        }
    });

    if (currentGroup.length > 0) {
        groupedArray.push(currentGroup);
    }

    return groupedArray;
}

/** Order groups by the minimum originalIndex of any item within each group. */
export function sortGroupedArray(groupedArray) {
    const groupMinIndexes = groupedArray.map(group => {
        const minIndex = group.reduce((min, item) => {
            if ('originalIndex' in item) {
                return Math.min(min, item.originalIndex);
            }
            return min;
        }, Infinity);
        return { group, minIndex };
    });

    groupMinIndexes.sort((a, b) => a.minIndex - b.minIndex);

    return groupMinIndexes.map(item => item.group);
}

/**
 * Build a map of category_key -> best (lowest) search rank, so a category can be
 * ranked by the most relevant element beneath it. Every element contributes its
 * rank to its own category_key AND to each key in its ancestor_keys, so a food —
 * which carries ancestor_keys but no category_key of its own — propagates its
 * search rank up to every category it lives under. An element's rank is its
 * originalIndex when present (the search-result position stamped by the caller),
 * otherwise its position in the array. (EAT-3929)
 */
export function buildKeyRankIndex(elements) {
    const rankByKey = {};

    // Category documents get a x10 relevance boost from the backend `avoidances` sort
    // and are returned ahead of foods, so a weak category name match (e.g. "eggplant"
    // for "eggs") would otherwise outrank the actual food matches and drag its parents
    // (Fruits, Vegetables) above Eggs. Rank foods ahead of category-only matches by
    // offsetting non-food ranks past every possible food rank: a category that actually
    // contains matching foods still wins via those foods, while a pure category match
    // stays ahead of the no-match tail. (EAT-3929)
    const categoryTierOffset = elements.length;

    elements.forEach((element, index) => {
        const baseRank = typeof element.originalIndex === 'number' ? element.originalIndex : index;
        const rank = element.type === 'food' ? baseRank : baseRank + categoryTierOffset;
        const keys = [
            ...(element.category_key ? [element.category_key] : []),
            ...(element.ancestor_keys || []),
        ];

        keys.forEach(key => {
            if (!(key in rankByKey) || rank < rankByKey[key]) {
                rankByKey[key] = rank;
            }
        });
    });

    return rankByKey;
}

/**
 * Stamp category nodes with the search rank of the best-matching element beneath
 * them, so top-level groups can be ordered by distance from the search term. A
 * node inherits the lowest rank of any element whose category_key matches it OR
 * whose ancestor_keys include it — so matching foods pull their top-level category
 * up even though foods have no category_key of their own. Categories with no match
 * anywhere in their subtree are left unstamped and sink to the end. (mutates category)
 */
export function assignOriginalIndex(category, elements) {
    const rankByKey = buildKeyRankIndex(elements);
    stampOriginalIndex(category, rankByKey);
    return category;
}

function stampOriginalIndex(category, rankByKey) {
    if (category.category_key in rankByKey) {
        category.originalIndex = rankByKey[category.category_key];
    }

    if (category.children && category.children.length > 0) {
        category.children.forEach(child => stampOriginalIndex(child, rankByKey));
    }
}

/** True if categoryKey is parentKeys or any of their ancestors (looked up in avoidResults). */
export function isDescendantOf(parentKeys, categoryKey, avoidResults) {
    if (!parentKeys?.length) {
        return false;
    }

    if (parentKeys.includes(categoryKey)) {
        return true;
    }

    const grandParentKeys = parentKeys.flatMap(parentKey => {
        const result = Object.values(avoidResults).find(item => item.category_key === parentKey);

        if (!result || !result.ancestor_keys) {
            return [];
        }

        return result.ancestor_keys;
    });

    return isDescendantOf(grandParentKeys, categoryKey, avoidResults);
}

/**
 * EAT-4017: flag each category option with whether it has any child in the
 * current (search-filtered) option set. Only flagged rows should render an
 * expand chevron; otherwise tapping a childless row is a dead no-op. Mutates
 * and returns the options array.
 */
export function markHasChildren(avoidOptions) {
    const parentKeysWithChildren = new Set(avoidOptions.flatMap(option => option.parents || []));

    avoidOptions.forEach(option => {
        option.hasChildren = !!option.categoryKey && parentKeysWithChildren.has(option.categoryKey);
    });

    return avoidOptions;
}
